// src/mobile_pages/system/MyPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReusableCalendar, {
  buildStatusCounts,
  getKRHolidayMap,
} from "../../components/common/feature/Calendar";
import { format } from "date-fns";
import { scheduleApi } from "../../api/schedules";
import BoardTable from "../../components/common/board/BoardTable";
import MobileSidePanel from "../../components/common/mobile/MobileSidePanel";
import ScheduleNew from "../../pages/communication/schedule/ScheduleNew";
import ApprovalWidget from "../../components/widgets/ApprovalWidget";
import TodoWidget from "../../components/widgets/TodoWidget";

export default function MobileMyPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchPersonalSchedules = useCallback(async () => {
    try {
      const res = await scheduleApi.listPersonal();
      setEvents(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error("❌ personal 일정 로드 실패", err);
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    fetchPersonalSchedules();
  }, [fetchPersonalSchedules]);

  const list = useMemo(
    () => events.filter((e) => e.start?.slice(0, 10) === dateStr),
    [events, dateStr]
  );

  const statusCountsByDate = useMemo(
    () =>
      buildStatusCounts(
        events,
        (m) => m.start,
        (m) =>
          m.scope === "personal"
            ? "green"
            : m.scope === "company"
              ? "red"
              : "blue"
      ),
    [events]
  );

  const [holidayMap, setHolidayMap] = useState({});
  useEffect(() => {
    (async () =>
      setHolidayMap(await getKRHolidayMap(selectedDate.getFullYear())))();
  }, [selectedDate]);

  const user =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user") || "{}")
      : {};

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner mobile-bottom-spacing space-y-4">
        {/* 상단 프로필 카드 */}
        <div className="bg-white rounded-lg p-4 border flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-semibold text-sm">
            {(user?.name || "U").slice(0, 1)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user?.name || "사용자"}
            </p>
            <p className="text-[11px] text-gray-500 truncate">
              마이페이지 · 대시보드
            </p>
          </div>
        </div>

        {/* 캘린더 */}
        <div className="section-card p-3">
          <ReusableCalendar
            value={selectedDate}
            onChange={setSelectedDate}
            statusCountsByDate={statusCountsByDate}
            holidayMap={holidayMap}
            showCounts={true}
            showHolidayLabels={false} // 모바일에서는 라벨 숨김
          />
        </div>

        {/* 일정 리스트 */}
        <div className="section-card p-3">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold ml-1">{dateStr} 개인 일정</h3>
            <button
              className="btn-create-sm"
              onClick={() => setPanelOpen(true)}
            >
              일정 등록
            </button>
          </div>
          <BoardTable
            keyField="id"
            rows={list}
            columns={[
              {
                key: "time",
                header: "시간",
                render: (row) => format(new Date(row.start), "HH:mm"),
              },
              { key: "title", header: "제목" },
            ]}
            emptyText="오늘은 일정이 없습니다."
          />
        </div>

        {/* Widgets */}
        <div className="space-y-4">
          <div className="section-card overflow-hidden">
            <ApprovalWidget />
          </div>
          <div className="section-card overflow-hidden">
            <TodoWidget />
          </div>
        </div>
      </div>

      <MobileSidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title="개인 일정 등록"
      >
        <ScheduleNew
          scheduleType="personal"
          onSaved={() => {
            fetchPersonalSchedules();
            setPanelOpen(false);
          }}
          onClose={() => setPanelOpen(false)}
        />
      </MobileSidePanel>
    </div>
  );
}

